import React, { useState, useEffect, useCallback, useRef } from 'react';
import { gsap } from 'gsap';
import styles from './FirstScreen.module.css';

// Import project images
import project1_3 from '../images/בית עתיק ציורי במושב אבן ספיר 1.jpg';
import project2_5 from '../images/הבית האקלקטי של הודיה ועוזיה 3.JPG';
import project3_7 from '../images/הבית של רתם ואורטל בבאר גנים 1.jpg';
import project4_2 from '../images/הבית האקלקטי של הודיה ועוזיה 11.JPG';
import project5_2 from '../images/הבית של שיר וגלעד ברמת גן 7.jpg';

// Hero slides data - interior design projects
const heroSlides = [
    {
    id: 3,
    image: project3_7,
  },
   {
    id: 4,
    image: project4_2,
  },

  {
    id: 1,
    image: project1_3,
  },
  {
    id: 2,
    image: project2_5,
  },
  
 
   {
    id: 5,
    image: project5_2,
  },
];

// Loading Animation Component - 3D House Building
const LoadingAnimation = () => {
  return (
    <div className={styles.loadingContainer}>
      <div className={styles.loadingScene}>
        {/* 3D House SVG Animation */}
        <svg 
          className={styles.houseSvg} 
          viewBox="0 0 200 180" 
          fill="none"
        >
          {/* Grid lines - architectural blueprint style */}
          <g className={styles.gridLines}>
            <line x1="0" y1="140" x2="200" y2="140" />
            <line x1="40" y1="0" x2="40" y2="180" />
            <line x1="100" y1="0" x2="100" y2="180" />
            <line x1="160" y1="0" x2="160" y2="180" />
            <line x1="0" y1="80" x2="200" y2="80" />
            {/* Diagonal construction line */}
            <line x1="10" y1="60" x2="50" y2="20" className={styles.diagonalLine} />
          </g>
          
          {/* House structure - builds progressively */}
          <g className={styles.houseStructure}>
            {/* Roof */}
            <path 
              className={styles.roof}
              d="M100 30 L40 80 L40 85 L100 35 L160 85 L160 80 Z"
            />
            
            {/* Main body - left section (black) */}
            <path 
              className={styles.mainBody}
              d="M40 80 L40 140 L100 140 L100 80"
            />
            
            {/* Right section - teal accent */}
            <rect 
              className={styles.accentSection}
              x="105" 
              y="100" 
              width="55" 
              height="25"
            />
            
            {/* Lower accent */}
            <rect 
              className={styles.accentLower}
              x="105" 
              y="130" 
              width="55" 
              height="15"
            />
          </g>
          
          {/* Construction points */}
          <g className={styles.constructionPoints}>
            <circle cx="100" cy="30" r="3" className={styles.point1} />
            <circle cx="40" cy="80" r="3" className={styles.point2} />
            <circle cx="160" cy="80" r="3" className={styles.point3} />
            <circle cx="40" cy="140" r="3" className={styles.point4} />
            <circle cx="160" cy="140" r="3" className={styles.point5} />
          </g>
        </svg>
        
        {/* Brand text */}
        <div className={styles.loadingBrand}>
          <span className={styles.loadingBrandName}>SG STUDIO</span>
          <span className={styles.loadingBrandSub}>architecture & interior design</span>
        </div>
        
        {/* Loading progress */}
        <div className={styles.loadingProgress}>
          <div className={styles.loadingProgressBar}></div>
        </div>
      </div>
    </div>
  );
};

const HeroCarousel = () => {
  const [currentSlide, setCurrentSlide] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [loadedImages, setLoadedImages] = useState(0);
  const autoplayRef = useRef(null);
  const progressRef = useRef(null);

  const SLIDE_DURATION = 6000; // 6 seconds per slide

  // Smooth scroll to element
  const scrollToElement = (elementId) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  // Preload images
  useEffect(() => {
    const imagePromises = heroSlides.map((slide) => {
      return new Promise((resolve) => {
        const img = new Image();
        img.src = slide.image;
        img.onload = () => {
          setLoadedImages(prev => prev + 1);
          resolve();
        };
        img.onerror = () => {
          setLoadedImages(prev => prev + 1);
          resolve();
        };
      });
    });

    Promise.all(imagePromises).then(() => {
      // Small delay for smooth transition
      setTimeout(() => {
        setIsLoading(false);
      }, 500);
    });
  }, []);

  // Progress bar animation
  const animateProgress = useCallback(() => {
    if (progressRef.current) {
      gsap.fromTo(progressRef.current,
        { scaleX: 0 },
        { scaleX: 1, duration: SLIDE_DURATION / 1000, ease: 'none' }
      );
    }
  }, []);

  // Go to specific slide
  const goToSlide = useCallback((index) => {
    if (isTransitioning || index === currentSlide) return;
    
    setIsTransitioning(true);
    setCurrentSlide(index);
    
    setTimeout(() => {
      setIsTransitioning(false);
    }, 800);
  }, [currentSlide, isTransitioning]);

  // Next slide
  const nextSlide = useCallback(() => {
    const next = currentSlide === heroSlides.length - 1 ? 0 : currentSlide + 1;
    goToSlide(next);
  }, [currentSlide, goToSlide]);

  // Previous slide
  const prevSlide = useCallback(() => {
    const prev = currentSlide === 0 ? heroSlides.length - 1 : currentSlide - 1;
    goToSlide(prev);
  }, [currentSlide, goToSlide]);

  // Autoplay
  useEffect(() => {
    autoplayRef.current = setInterval(() => {
      nextSlide();
    }, SLIDE_DURATION);

    return () => {
      if (autoplayRef.current) {
        clearInterval(autoplayRef.current);
      }
    };
  }, [nextSlide]);

  // Animate on slide change
  useEffect(() => {
    animateProgress();
  }, [currentSlide, animateProgress]);

  // Reset autoplay on manual navigation
  const handleManualNav = (action) => {
    if (autoplayRef.current) {
      clearInterval(autoplayRef.current);
    }
    action();
    autoplayRef.current = setInterval(() => {
      nextSlide();
    }, SLIDE_DURATION);
  };

  // Show loading animation while images load
  if (isLoading) {
    return <LoadingAnimation />;
  }

  return (
    <section className={styles.heroSection}>
      {/* Background Images */}
      <div className={styles.slidesContainer}>
        {heroSlides.map((slide, index) => (
          <div
            key={slide.id}
            className={`${styles.slide} ${index === currentSlide ? styles.slideActive : ''}`}
          >
            <div className={styles.imageWrapper}>
              <img
                src={slide.image}
                alt=""
                className={styles.slideImage}
              />
            </div>
          </div>
        ))}
        {/* Overlay */}
        <div className={styles.overlay}></div>
      </div>

      {/* Content */}
      <div className={styles.heroContent}>
        <div className={styles.contentInner}>
          {/* Logo/Brand */}
          <div className={styles.brandMark}>
            <span className={styles.brandLine}></span>
            <span className={styles.brandText}>sg studio - arch & design</span>
            <span className={styles.brandLine}></span>
          </div>

          {/* Main Text - Fixed Slogan */}
          <h1 className={styles.heroTitle}>
            לעצב בית של <span className={styles.highlight}>מגזין</span>
            <br />
            בלי לשבור חסכונות
          </h1>
          <p className={styles.heroSubtitle}>
            תכנון אדריכלי ועיצוב פנים שרואה כל סנטימטר בבית - וכל שקל בתקציב שלך.
          </p>

          {/* CTA Buttons - Stacked */}
          <div className={styles.ctaContainer}>
            <a 
              href="#all-projects"
              className={styles.ctaButton}
              onClick={(e) => {
                e.preventDefault();
                scrollToElement('all-projects');
              }}
            >
              <span className={styles.ctaIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M4 6h16M4 12h16M4 18h16"/>
                </svg>
              </span>
              <span className={styles.ctaText}>הפרויקטים שלי</span>
              <span className={styles.ctaArrow}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
              </span>
            </a>
            <a href="https://wa.me/972543324747?text=%D7%94%D7%99%D7%99%20%D7%A9%D7%99%D7%A8%D7%9C%D7%99%20%D7%94%D7%92%D7%A2%D7%AA%D7%99%20%D7%90%D7%9C%D7%99%D7%99%D7%9A%20%D7%93%D7%A8%D7%9A%20%D7%94%D7%90%D7%AA%D7%A8%2C%20%D7%90%D7%A9%D7%9E%D7%97%20%D7%9C%D7%A9%D7%9E%D7%95%D7%A2%20%D7%9E%D7%9E%D7%9A%20%D7%A2%D7%95%D7%93%20%D7%A2%D7%9C.." target="_blank" rel="noopener noreferrer" className={`${styles.ctaButton} ${styles.ctaPrimary}`}>
              <span className={styles.ctaIcon}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"/>
                </svg>
              </span>
              <span className={styles.ctaText}>בואו נדבר</span>
              <span className={styles.ctaArrow}>
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                  <path d="M19 12H5M12 5l-7 7 7 7"/>
                </svg>
              </span>
            </a>
          </div>
        </div>

        {/* Decorative Elements */}
        <div className={styles.decorCorner} data-position="top-right"></div>
        <div className={styles.decorCorner} data-position="bottom-left"></div>
      </div>

      {/* Navigation */}
      <div className={styles.navigation}>
        {/* Slide Counter */}
        <div className={styles.slideCounter}>
          <span className={styles.currentNum}>
            {String(currentSlide + 1).padStart(2, '0')}
          </span>
          <span className={styles.counterDivider}></span>
          <span className={styles.totalNum}>
            {String(heroSlides.length).padStart(2, '0')}
          </span>
        </div>

        {/* Progress Bar */}
        <div className={styles.progressContainer}>
          <div ref={progressRef} className={styles.progressBar}></div>
        </div>

        {/* Nav Arrows */}
        <div className={styles.navArrows}>
          <button 
            className={styles.navArrow} 
            onClick={() => handleManualNav(prevSlide)}
            aria-label="Previous slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 18l6-6-6-6"/>
            </svg>
          </button>
          <button 
            className={styles.navArrow} 
            onClick={() => handleManualNav(nextSlide)}
            aria-label="Next slide"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M15 18l-6-6 6-6"/>
            </svg>
          </button>
        </div>
      </div>

      {/* Dots Navigation */}
      <div className={styles.dotsNav}>
        {heroSlides.map((_, index) => (
          <button
            key={index}
            className={`${styles.dot} ${index === currentSlide ? styles.dotActive : ''}`}
            onClick={() => handleManualNav(() => goToSlide(index))}
            aria-label={`Go to slide ${index + 1}`}
          />
        ))}
      </div>

      {/* Scroll Indicator */}
      <div className={styles.scrollIndicator}>
        <span className={styles.scrollText}>גלול למטה</span>
        <div className={styles.scrollLine}>
          <div className={styles.scrollDot}></div>
        </div>
      </div>
    </section>
  );
};

export default HeroCarousel;